import {
  EASYLOGIN_STATUS,
  ORGANIZATION_STATUS,
  type IAnalyticsBootFeature,
  type IAnalyticsBootResponse,
  type IAnalyticsLocation,
  type IAnalyticsManager,
  type IEngagementEvent,
  type IGoal,
  type IImpressionEvent,
  type ILeadEvent,
  type IPageviewEvent,
  type IReactionEvent,
  type ISessionStartEvent,
  type IUserDefinedEvent,
  type ReactionEventData,
} from "@shared/common/types/sdk";
import {
  convertUUIDToUint64,
  randomUInt64Number,
} from "@shared/common/utils/uint64";
import Cookies from "js-cookie";
import { getDevice } from "./component-manager";
import type { InsightRequest } from "./create-queue-based-send";
import { createQueueBasedSend } from "./create-queue-based-send";
import type { UserData } from "./data";
import type { IDeduplicationSet } from "./deduplication-set";
import { getConversionId } from "./lib/conversion-id";
import { getUserTrackingToken } from "./lib/user-tracking-token";
import type { IVisitorDataLoader } from "./sites/visitor-data-loader";
import type { State } from "./state";
import type { IRecordEventQueue } from "./type";
import { logDebug } from "./util/common";
import { getTopWindow } from "./util/get-top-window";

interface ICommonFormDataArgs {
  siteId: string;
  diagnosticData?: unknown;
  eventKey?: string;
}

const ANALYTICS_ENDPOINT = `${ANALYTICS_API_URL}/api/v1/event?_v=${SDK_VERSION}`;
const ANALYTICS_BOOT_ENDPOINT = `${ANALYTICS_API_URL}/api/v1/event/boot?_v=${SDK_VERSION}`;
const EXPIRE_30MIN = 1 / (24 * 2);

const sendAnalyticsEvent = async (
  req: InsightRequest
): Promise<XMLHttpRequest | null> => {
  // production 빌드인데 localhost로 보내는 경우는 무시
  if (NODE_ENV === "production" && ANALYTICS_ENDPOINT.includes("localhost")) {
    return null;
  }

  const formData = new FormData();

  Object.keys(req.formData).forEach((key) => {
    formData.append(key, req.formData[key]!);
  });

  // 페이지 이탈시 완료되지 않은 이벤트 요청이 취소되는 것을 방지하기 위해 sendBeacon을 사용
  if (window.navigator && typeof window.navigator.sendBeacon === "function") {
    if (req.requestType === "NORMAL") {
      const sendBeaconRes = navigator.sendBeacon(req.url, formData);
      if (sendBeaconRes) {
        // queue에서 최소한의 순서 보장을 위해 50ms 대기시간을 가진 promise를 반환
        return new Promise((resolve) => {
          setTimeout(() => {
            resolve(null);
          }, 50);
        });
      }
      // 실패한 경우에는 xhr로 요청 시도
    }
  }

  // CHANGE_USER 타입은 이후에 location 정보 등 추가적인 정보를 받아오기 위해 XMLHttpRequest를 사용
  return new Promise<XMLHttpRequest>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", req.url, true);
    xhr.onload = () => {
      return resolve(xhr);
    };
    xhr.onerror = reject;
    xhr.send(formData);
  });
};

export const getReactionEventName = ({
  action,
  nodeName,
}: ReactionEventData) => {
  const result = `${
    action.type === "EVAL_JS" ||
    action.type === "AIRBRIDGE:openDeeplink" ||
    action.type === "URL" ||
    action.type === "GOTO_PAGE"
      ? "click"
      : "event"
  }|${action.type}|${nodeName ?? ""}`;

  return result;
};

const _getCommonFormData = (
  args: ICommonFormDataArgs
): Record<string, string> => {
  const { siteId } = args;

  const result: Record<string, string> = {
    url: getTopWindow().location.href,
    siteId,
    deviceId: _getDeviceId(),
    sessionId: _getSessionId(),
    device: getDevice(),
    referrer: getTopWindow().document.referrer,
  };

  if (args.diagnosticData) {
    result.diagnosticData = JSON.stringify(args.diagnosticData);
  }

  if (args.eventKey) {
    result.eventKey = args.eventKey;
  }

  const conversionId = getConversionId();
  if (conversionId) {
    result.conversionId = conversionId;
  }

  const userTrackingToken = getUserTrackingToken();
  if (userTrackingToken) {
    result.userTrackingToken = userTrackingToken;
  }

  return result;
};

/** TODO: userId 받는게 느리다면 Boot도 덩달아 느려지는 문제가 있음. */
export const getUserRelatedFormData = async (
  user: UserData
): Promise<Record<string, string>> => {
  return {
    loginId: (await user.loginId.get()) ?? "",
  };
};

/** 신뢰할 수 없는 Partial 타입을 extends 조건으로 모두 직접 지정. 필드가 추가되면 본 타입도 수정해야 함 */
export type PartialAnalyticsBootResponseData<T> = T extends IAnalyticsLocation
  ? Partial<IAnalyticsLocation>
  : T extends IGoal
  ? IGoal // Goal 의 Partial 데이터는 없다고 가정
  : T extends IAnalyticsBootResponse
  ? {
      location?: PartialAnalyticsBootResponseData<IAnalyticsLocation>;
      goals?: Array<PartialAnalyticsBootResponseData<IGoal>>;
      organizationStatus?: ORGANIZATION_STATUS;
      easyLoginStatus?: EASYLOGIN_STATUS;
      isNewUser?: boolean;
      disableListener?: boolean;
      features?: IAnalyticsBootFeature;
      utm?: {
        utmSource?: string;
        utmMedium?: string;
        utmCampaign?: string;
        utmTerm?: string;
        utmContent?: string;
      };
    }
  : never;

export const createAnalyticsLocation = (
  data?: PartialAnalyticsBootResponseData<IAnalyticsLocation>
): IAnalyticsLocation => {
  return {
    ip: "",
    postal: "",
    region: "",
    city: "",
    country: "",
    ...data,
  };
};

export const createAnalyticsBootResponse = (
  data?: PartialAnalyticsBootResponseData<IAnalyticsBootResponse>
): IAnalyticsBootResponse => {
  return {
    location: createAnalyticsLocation(data?.location),
    goals: data?.goals || [],
    organizationStatus:
      typeof data?.organizationStatus === "number"
        ? data.organizationStatus
        : ORGANIZATION_STATUS.ACTIVE,
    easyLoginStatus:
      typeof data?.easyLoginStatus === "number"
        ? data.easyLoginStatus
        : EASYLOGIN_STATUS.ACTIVE,
    isNewUser: data?.isNewUser || false,
    disableListener: data?.disableListener || false,
    features: data?.features || {},
    utm: data?.utm || {},
  };
};

/** Analytics Boot 응답 BODY JSON.parse 결과 타입 */
type BootResponseJson = {
  goals?: IGoal[];
  organizationStatus?: ORGANIZATION_STATUS;
  easyLoginStatus?: EASYLOGIN_STATUS;
  isNewUser?: boolean;
  disableListener?: boolean;
  features?: IAnalyticsBootFeature;
  utm?: {
    utmSource?: string;
    utmMedium?: string;
    utmCampaign?: string;
    utmTerm?: string;
    utmContent?: string;
  };
};

/**
 * Analytics Boot 응답으로부터 추가적인 정보를 파싱해옵니다. boot 요청은 최초 스크립트 로딩시 한번만 이루어지는 특성으로 인해
 * 본래 통계 목적 외에 사용자의 Location 정보나 사이트의 동적 상태 등 부가적인 정보를 가져오는 데에도 사용됩니다.
 */
export const parseAnalyticsBootResponse = (
  xhr: XMLHttpRequest | null
): IAnalyticsBootResponse => {
  const result = createAnalyticsBootResponse();

  if (!xhr) {
    return result;
  }

  // Location 정보 대입
  try {
    const base64LocationInfo = xhr.getResponseHeader("Location");
    if (base64LocationInfo) {
      result.location = createAnalyticsLocation(
        JSON.parse(
          window.atob(base64LocationInfo)
        ) as PartialAnalyticsBootResponseData<IAnalyticsLocation>
      );
    }
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error(e);
    result.location = {
      ip: "",
      postal: "",
      region: "",
      city: "",
      country: "",
    };
  }

  try {
    const body = xhr.response;
    if (body) {
      const res = JSON.parse(body) as BootResponseJson;
      // 사이트에 설정한 지표 트리거 정보
      result.goals = res.goals || [];
      // 사이트가 속한 팀의 상태 정보
      result.organizationStatus =
        typeof res.organizationStatus === "number"
          ? res.organizationStatus
          : ORGANIZATION_STATUS.ACTIVE;
      // 사이트가 속한 팀의 간편로그인 상태 정보
      result.easyLoginStatus =
        typeof res.easyLoginStatus === "number"
          ? res.easyLoginStatus
          : EASYLOGIN_STATUS.ACTIVE;
      // 신규 사용자 여부
      result.isNewUser = res.isNewUser || false;
      // listener 비활성화 여부
      result.disableListener = res.disableListener || false;
      // 추가 기능
      result.features = res.features || {};
      // UTM 데이터
      result.utm = res.utm || {};
    }
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error(e);
  }

  return result;
};

const generateAnalyticsPluginEvent = (data: unknown) => {
  return {
    type: 6,
    data: {
      plugin: "analytics-event",
      payload: {
        type: "custom",
        data,
      },
    },
    timestamp: Date.now(),
  };
};

export const createAnalyticsManager = ({
  user,
  visitorDataLoaderState,
  recordEventQueue,
  eventDeduplicationSet,
}: {
  user: UserData;
  visitorDataLoaderState: State<IVisitorDataLoader>;
  recordEventQueue?: IRecordEventQueue | undefined;
  eventDeduplicationSet: IDeduplicationSet;
}): IAnalyticsManager => {
  let __isEntry = isFirstVisit();
  let __ignoreAnalyticsEvent = false;
  let __lastSessionId = _getSessionId();

  const queueBasedSendAnalyticsEvent = createQueueBasedSend(sendAnalyticsEvent);

  /**
   * visitorDataLoaderState.getCurrentPageInfo() 로부터 페이지 정보를 받아와서
   * 기존 이벤트 meta와 병합합니다. 키가 충돌할 경우 이벤트 meta가 우선됩니다.
   *
   * pageInfo.type === "UNKNOWN"인 경우는 병합하지 않습니다.
   *
   */
  const composePageInfoMeta = async (
    originalMeta?: Record<string, unknown>
  ): Promise<Record<string, unknown> | undefined> => {
    // type 속성 제거
    const { type, ...restPageInfo } = await (
      await visitorDataLoaderState.get()
    ).getCurrentPageInfo();

    if (type === "UNKNOWN") {
      return originalMeta;
    }

    return {
      pageType: type,
      ...restPageInfo,
      ...originalMeta,
    };
  };

  function isValidUserDefinedEventName(name: string) {
    if (
      name === "pageview" ||
      name === "impression" ||
      name === "click" ||
      name === "event"
    ) {
      return {
        success: false,
        reason: `${name}을 이벤트 이름으로 사용할 수 없습니다.`,
      };
    }

    if (name.startsWith("click|") || name.startsWith("event|")) {
      return {
        success: false,
        reason: `${name}으로 시작하는 이벤트 이름은 사용할 수 없습니다.`,
      };
    }

    return { success: true };
  }

  async function sendSessionStartEvent(event: ISessionStartEvent) {
    const formData: Record<string, string> = {
      name: "session",
      ..._getCommonFormData(event),
      ...(await getUserRelatedFormData(user)),
    };

    if (event.meta) {
      formData.meta = JSON.stringify(event.meta);
    }

    recordEventQueue?.addEvent(
      generateAnalyticsPluginEvent({
        tag: event.type,
        payload: {
          name: "session",
        },
      })
    );

    return queueBasedSendAnalyticsEvent({
      requestType: "NORMAL",
      url: ANALYTICS_ENDPOINT,
      formData,
    });
  }

  async function sendPageviewEvent(event: IPageviewEvent) {
    const formData: Record<string, string> = {
      name: "pageview",
      ..._getCommonFormData(event),
      ...(await getUserRelatedFormData(user)),
    };

    if (event.meta) {
      formData.meta = JSON.stringify(event.meta);
    }

    recordEventQueue?.addEvent(
      generateAnalyticsPluginEvent({
        tag: event.type,
        payload: {
          name: "pageview",
        },
      })
    );

    return queueBasedSendAnalyticsEvent({
      requestType: "NORMAL",
      url: ANALYTICS_ENDPOINT,
      formData,
    });
  }

  async function sendImpressionEvent(event: IImpressionEvent) {
    const formData: Record<string, string> = {
      name: "impression",
      ..._getCommonFormData(event),
      ...(await getUserRelatedFormData(user)),
      campaignId: event.campaignId,
      campaignType: event.campaignType,
    };

    if (event.parentCampaignId) {
      formData.parentCampaignId = event.parentCampaignId;
    }
    if (event.meta) {
      formData.meta = JSON.stringify(event.meta);
    }

    logDebug(
      `AnalyticsManager: sendImpressionEvent - ${event.parentCampaignId}/${event.campaignId}(${event.campaignType})`
    );

    recordEventQueue?.addEvent(
      generateAnalyticsPluginEvent({
        tag: event.type,
        payload: {
          name: "impression",
          campaignId: event.campaignId,
          campaignType: event.campaignType,
          parentCampaignId: event.parentCampaignId,
        },
      })
    );

    return queueBasedSendAnalyticsEvent({
      requestType: "NORMAL",
      url: ANALYTICS_ENDPOINT,
      formData,
    });
  }

  async function sendEngagementEvent(event: IEngagementEvent) {
    const formData: Record<string, string> = {
      name: "engagement",
      ..._getCommonFormData(event),
      ...(await getUserRelatedFormData(user)),
      ...event.commonFormData,
      scrollDepth: `${event.scrollDepth}`,
      engagementTime: `${event.engagementTime}`,
    };

    if (event.meta) {
      formData.meta = JSON.stringify(event.meta);
    }

    return queueBasedSendAnalyticsEvent({
      requestType: "NORMAL",
      url: ANALYTICS_ENDPOINT,
      formData,
    });
  }

  async function sendReactionEvent(event: IReactionEvent) {
    const formData: Record<string, string> = {
      name: getReactionEventName(event.data),
      ..._getCommonFormData(event),
      ...(await getUserRelatedFormData(user)),
      campaignId: event.campaignId,
      campaignType: event.campaignType,
    };

    if (event.parentCampaignId) {
      formData.parentCampaignId = event.parentCampaignId;
    }

    if (event.meta) {
      formData.meta = JSON.stringify(event.meta);
    }

    recordEventQueue?.addEvent(
      generateAnalyticsPluginEvent({
        tag: event.type,
        payload: {
          name: getReactionEventName(event.data),
          campaignId: event.campaignId,
          campaignType: event.campaignType,
          parentCampaignId: event.parentCampaignId,
        },
      })
    );

    return queueBasedSendAnalyticsEvent({
      requestType: "NORMAL",
      url: ANALYTICS_ENDPOINT,
      formData,
    });
  }

  /**
   * FormData의 최종 형태
   *
   * ```json
   * {
   *   "name": event.name,
   *   "url": "https://example.com",
   *   "siteId": "site_public_id",
   *   "deviceId": "device_id",
   *   "sessionId": "session_id",
   *   "device": "DESKTOP",
   *   "referrer": "https://referrer.com",
   *   "eventKey": "{sitePublicId}-{orderId}-{itemId?}",
   *   "loginId": "user_login_id",
   *   "revenueAmount": "1000",
   *   "revenueCurrency": "KRW",
   *   "discountAmount": "100",
   *   "shippingFeeAmount": "200",
   *   "mileageAmount": "50",
   *   "meta": "JSON.stringify(event.props)",
   *   "diagnosticData": "JSON.stringify({...})",
   * }
   * ```
   */
  async function sendUserDefinedEvent(event: IUserDefinedEvent) {
    const validation = isValidUserDefinedEventName(event.name);
    if (!validation.success) {
      // eslint-disable-next-line no-console
      console.warn(validation.reason);
      return;
    }

    const formData: Record<string, string> = {
      name: event.name,
      ..._getCommonFormData(event),
      ...(await getUserRelatedFormData(user)),
      loginId: (await user.loginId.get()) ?? event.loginId ?? "",
    };

    if (event.revenue) {
      formData.revenueAmount = `${event.revenue.amount}`;
      if (event.revenue.currency) {
        formData.revenueCurrency = event.revenue.currency;
      }
    }

    if (event.discount) {
      formData.discountAmount = `${event.discount.amount}`;
    }

    if (event.shippingFee) {
      formData.shippingFeeAmount = `${event.shippingFee.amount}`;
    }

    if (event.mileage) {
      formData.mileageAmount = `${event.mileage.amount}`;
    }

    if (event.meta) {
      formData.meta = JSON.stringify(event.meta);
    }

    recordEventQueue?.addEvent(
      generateAnalyticsPluginEvent({
        tag: event.type,
        payload: {
          name: event.name,
          revenueAmount: event.revenue ? `${event.revenue.amount}` : undefined,
          revenueCurrency:
            event.revenue && event.revenue.currency
              ? event.revenue.currency
              : undefined,
          meta: event.meta ? JSON.stringify(event.meta) : undefined,
        },
      })
    );

    await queueBasedSendAnalyticsEvent({
      requestType: "NORMAL",
      url: ANALYTICS_ENDPOINT,
      formData,
    });
  }

  async function sendLeadEvent(event: ILeadEvent) {
    const formData: Record<string, string> = {
      name: `lead|${event.campaignType}`,
      ..._getCommonFormData(event),
      ...(await getUserRelatedFormData(user)),
      campaignId: event.campaignId,
      campaignType: event.campaignType,
    };

    if (event.parentCampaignId) {
      formData.parentCampaignId = event.parentCampaignId;
    }

    if (event.meta) {
      formData.meta = JSON.stringify(event.meta);
    }

    recordEventQueue?.addEvent(
      generateAnalyticsPluginEvent({
        tag: event.type,
        payload: {
          name: `lead|${event.campaignType}`,
          campaignId: event.campaignId,
          campaignType: event.campaignType,
          parentCampaignId: event.parentCampaignId,
        },
      })
    );

    return queueBasedSendAnalyticsEvent({
      requestType: "NORMAL",
      url: ANALYTICS_ENDPOINT,
      formData,
    });
  }

  async function handleCheckSessionIdChange(siteId: string) {
    const currentSessionId = _getSessionId();

    if (currentSessionId !== __lastSessionId) {
      __lastSessionId = currentSessionId;

      await sendSessionStartEvent({
        type: "SESSION_START",
        siteId,
      });
    }
  }

  return {
    async sendBoot(args) {
      const payload: InsightRequest = {
        requestType: "CHANGE_USER",
        url: ANALYTICS_BOOT_ENDPOINT,
        formData: {
          ..._getCommonFormData({ siteId: args.siteId }),
          ...(await getUserRelatedFormData(user)),
        },
      };

      if (args.ignoreAnalyticsEvent) {
        payload.formData.ignoreAnalyticsEvent = "true";
      }

      const xhr = await queueBasedSendAnalyticsEvent(payload);

      return parseAnalyticsBootResponse(xhr);
    },

    async sendSetUser() {
      // TODO: 코드 추가
      return;
    },

    async sendResetUser() {
      // TODO: 코드 추가
      return;
    },

    async sendAnalyticsEvent(event) {
      // iframe에서 parent와 같은 이벤트를 여러번 보내는 것 방지
      // TODO: iframe을 이용한 instagram-widget을 사용한다면 이 부분을 수정해야 함
      const topWindow = getTopWindow();
      if (topWindow !== window.self) {
        return;
      }

      if (__ignoreAnalyticsEvent) {
        return;
      }

      if (event.eventKey) {
        if (eventDeduplicationSet.has(event.eventKey)) {
          return;
        }
        eventDeduplicationSet.add(event.eventKey);
      }

      event.meta = await composePageInfoMeta(event.meta);

      await handleCheckSessionIdChange(event.siteId);

      switch (event.type) {
        case "SESSION_START":
          await sendSessionStartEvent(event);
          break;
        case "PAGEVIEW":
          await sendPageviewEvent(event);
          break;
        case "IMPRESSION":
          await sendImpressionEvent(event);
          break;
        case "ENGAGEMENT":
          await sendEngagementEvent(event);
          break;
        case "REACTION":
          await sendReactionEvent(event);
          break;
        case "USER_DEFINED_EVENT":
          await sendUserDefinedEvent(event);
          break;
        case "LEAD":
          await sendLeadEvent(event);
          break;
      }
    },

    async checkSessionIdChange(siteId: string) {
      await handleCheckSessionIdChange(siteId);
    },

    setIsEntry(isEntry: boolean) {
      __isEntry = isEntry;
    },

    setIgnoreAnalyticsEvent(ignore: boolean) {
      __ignoreAnalyticsEvent = ignore;
    },

    getDeviceId() {
      return _getDeviceId();
    },

    getSessionId() {
      return _getSessionId();
    },

    getCommonFormData(args: ICommonFormDataArgs) {
      return _getCommonFormData(args);
    },
  };
};

const _getDeviceId = () => {
  let deviceId = Cookies.get("_cnb");
  if (!deviceId) {
    deviceId = createNewId();
  }

  if (deviceId.length > 20) {
    deviceId = convertUUIDToUint64(deviceId);
  }

  Cookies.set("_cnb", deviceId, { expires: 730 });
  return deviceId;
};

const _getSessionId = () => {
  let sessionId = Cookies.get("_cnbsid");
  if (!sessionId) {
    sessionId = createNewId();
  }

  if (sessionId.length > 20) {
    sessionId = convertUUIDToUint64(sessionId);
  }

  Cookies.set("_cnbsid", sessionId, { expires: EXPIRE_30MIN });
  return sessionId;
};

const isFirstVisit = () => {
  const deviceId = Cookies.get("_cnb");
  return !deviceId;
};

const createNewId = () => {
  return randomUInt64Number();
};
